/**
 * Firebase Configuration Loader
 *
 * This module handles loading Firebase configuration from two sources:
 * 1. Development: Vite environment variables (from .env file)
 * 2. Production: Runtime config (from public/config.js)
 *
 * The runtime config approach is necessary for Firebase Hosting because
 * Vite embeds environment variables at build time, and .env files are
 * not available in production builds.
 */

/**
 * Load configuration from runtime config or environment variables
 * @returns {Object} Firebase configuration object
 */
export function getFirebaseConfig() {
  // Try runtime config first (production)
  if (typeof window !== 'undefined' && window.__TRAVAILI_CONFIG__) {
    const config = window.__TRAVAILI_CONFIG__;
    return {
      apiKey: config.apiKey,
      authDomain: config.authDomain,
      projectId: config.projectId,
      storageBucket: config.storageBucket,
      messagingSenderId: config.messagingSenderId,
      appId: config.appId
    };
  }

  // Fall back to Vite environment variables (development)
  // Support both import.meta.env (Vite) and process.env (Node.js tests)
  const env = (typeof process !== 'undefined' && process.env) || import.meta.env || {};
  return {
    apiKey: env.VITE_FIREBASE_API_KEY,
    authDomain: env.VITE_FIREBASE_AUTH_DOMAIN,
    projectId: env.VITE_FIREBASE_PROJECT_ID,
    storageBucket: env.VITE_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: env.VITE_FIREBASE_MESSAGING_SENDER_ID,
    appId: env.VITE_FIREBASE_APP_ID
  };
}

/**
 * Get reCAPTCHA site key
 * @returns {string|undefined} reCAPTCHA site key
 */
export function getRecaptchaSiteKey() {
  if (typeof window !== 'undefined' && window.__TRAVAILI_CONFIG__) {
    return window.__TRAVAILI_CONFIG__.recaptchaSiteKey;
  }
  const env = (typeof process !== 'undefined' && process.env) || import.meta.env || {};
  return env.VITE_RECAPTCHA_SITE_KEY;
}

/**
 * Get Google Maps API key
 * @returns {string|undefined} Google Maps API key
 */
export function getGoogleMapsApiKey() {
  if (typeof window !== 'undefined' && window.__TRAVAILI_CONFIG__) {
    return window.__TRAVAILI_CONFIG__.googleMapsApiKey;
  }
  const env = (typeof process !== 'undefined' && process.env) || import.meta.env || {};
  return env.VITE_GOOGLE_MAPS_API_KEY;
}

/**
 * Get OpenAI model configuration
 * @returns {string} OpenAI model name
 */
export function getOpenAIModel() {
  if (typeof window !== 'undefined' && window.__TRAVAILI_CONFIG__) {
    return window.__TRAVAILI_CONFIG__.openaiModel || 'gpt-4o-mini';
  }
  const env = (typeof process !== 'undefined' && process.env) || import.meta.env || {};
  return env.VITE_OPENAI_MODEL || 'gpt-4o-mini';
}

/**
 * Get RapidAPI configuration
 * @returns {Object} RapidAPI configuration
 */
export function getRapidApiConfig() {
  if (typeof window !== 'undefined' && window.__TRAVAILI_CONFIG__) {
    return {
      key: window.__TRAVAILI_CONFIG__.rapidApiKey,
      host: window.__TRAVAILI_CONFIG__.rapidApiHost
    };
  }
  const env = (typeof process !== 'undefined' && process.env) || import.meta.env || {};
  return {
    key: env.VITE_RAPIDAPI_KEY,
    host: env.VITE_RAPIDAPI_HOST
  };
}

/**
 * Validate that all required configuration values are present
 * @returns {Object} Validation result with isValid flag and missing keys
 */
export function validateConfig() {
  const config = getFirebaseConfig();
  const requiredKeys = [
    'apiKey',
    'authDomain',
    'projectId',
    'storageBucket',
    'messagingSenderId',
    'appId'
  ];

  const missingKeys = requiredKeys.filter(key => !config[key]);

  return {
    isValid: missingKeys.length === 0,
    missingKeys,
    config
  };
}
